import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { IoChevronBack } from "react-icons/io5";
import ProjectUpdateForm from "./ProjectUpdateForm";
import {
  createScopeUpdate,
  updateScopeUpdate,
  uploadUpdatesFile,
  getScopeUpdateDetails,
} from "../../../../../services/scopeUpdateService";
import "./ProjectUpdateDetails.css";

const TAB_ORDER = ["summary", "activities", "milestones", "issues", "review"];

const getNextTab = (current) => {
  const i = TAB_ORDER.indexOf(current);
  if (i === -1 || i >= TAB_ORDER.length - 1) return null;
  return TAB_ORDER[i + 1];
};
const getPrevTab = (current) => {
  const i = TAB_ORDER.indexOf(current);
  if (i <= 0) return null;
  return TAB_ORDER[i - 1];
};

export default function ProjectUpdateDetails({
  projectId,
  projectDetails,
  updateIdFromUrl,
  updateDateFromUrl,
  scopeIdFromUrl,
  onBack,
}) {
  const isCreateMode = updateIdFromUrl === "new";
  const [createdUpdateId, setCreatedUpdateId] = useState(null);
  const [isViewMode, setIsViewMode] = useState(!isCreateMode);
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    date: updateDateFromUrl ? new Date(updateDateFromUrl) : new Date(),
    description: "",
    scope: null,
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedMilestones, setSelectedMilestones] = useState([]);
  const [milestoneProgress, setMilestoneProgress] = useState({});
  const [milestoneNotes, setMilestoneNotes] = useState({});
  const [activitiesForPayload, setActivitiesForPayload] = useState([]);

  const [singleUpdateData, setSingleUpdateData] = useState(null);

  const effectiveUpdateId =
    createdUpdateId || (updateIdFromUrl !== "new" ? updateIdFromUrl : null);
  const hasUpdateId = !!effectiveUpdateId;

  const refetchSingleUpdate = useCallback(async () => {
    if (!effectiveUpdateId) return null;
    try {
      const res = await getScopeUpdateDetails(effectiveUpdateId);
      const data = Array.isArray(res?.data) ? res.data[0] : res?.data ?? res;
      setSingleUpdateData(data);
      return data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [effectiveUpdateId]);

  useEffect(() => {
    refetchSingleUpdate();
  }, [refetchSingleUpdate]);

  const refDataMilestones = singleUpdateData?.ref_data ?? [];

  const buildSanitizedMilestones = useCallback(
    (refArray) => {
      const milestoneIdsFromImages = [
        ...new Set(
          (uploadedFiles || []).flatMap((f) =>
            (f.meta_data?.milestones || []).map((m) => m.id).filter(Boolean)
          )
        ),
      ];
      const selectedIds = new Set(selectedMilestones.map((m) => m.id));
      const fromImagesOnly = milestoneIdsFromImages.filter(
        (id) => !selectedIds.has(id)
      );
      const activitiesToAdd = fromImagesOnly
        .map((id) => activitiesForPayload.find((a) => a.id === id))
        .filter(Boolean);
      const mergedMilestones = [...selectedMilestones, ...activitiesToAdd];

      const plannedProgressById =
        projectDetails?.milestone?.reduce((acc, m) => {
          if (m?.id != null) acc[m.id] = m.milestone_percentage ?? 0;
          return acc;
        }, {}) || {};

      return mergedMilestones.map(({ budget, ...rest }) => {
        const note = (milestoneNotes[rest.id] ?? "").trim();
        const refItem = (refArray || []).find(
          (r) => r.id === rest.id || r.id == rest.id
        );

        let previous_progress;
        let progress;

        if (refItem) {
          const refProgress = refItem.progress ?? 0;
          const refPrevious =
            refItem.previous_progress ?? refItem.progress ?? 0;
          const userProgress = milestoneProgress[rest.id];
          progress = userProgress !== undefined ? userProgress : refProgress;
          previous_progress =
            userProgress !== undefined && userProgress !== refProgress
              ? refProgress
              : refPrevious;
        } else {
          const fromRest = rest.progress ?? rest.milestone_percentage ?? 0;
          const fromRestPrevious = rest.previous_progress ?? fromRest;
          progress = milestoneProgress[rest.id] ?? fromRest;
          previous_progress =
            milestoneProgress[rest.id] !== undefined &&
            milestoneProgress[rest.id] !== fromRest
              ? fromRest
              : fromRestPrevious;
        }

        return {
          ...rest,
          previous_progress,
          progress,
          planned_progress: plannedProgressById[rest.id] ?? 0,
          ...(note ? { notes: note } : {}),
        };
      });
    },
    [
      uploadedFiles,
      selectedMilestones,
      activitiesForPayload,
      milestoneProgress,
      milestoneNotes,
      projectDetails,
    ]
  );

  const uploadPendingFiles = useCallback(
    async (refId) => {
      const newFiles = uploadedFiles.filter((f) => !f.id && f.file);
      for (const f of newFiles) {
        await uploadUpdatesFile({
          refId,
          file: f.file,
          metaData: f.meta_data || { room: null, milestones: [] },
        });
      }
    },
    [uploadedFiles]
  );

  const handleSummarySubmit = async () => {
    if (!form.name?.trim()) return toast.error("Update title is required");
    if (!form.description?.trim())
      return toast.error("Progress description is required");
    if (!form.date) return toast.error("Update date is required");

    const scopeId = form.scope?.scope_id;
    const bidId = form.scope?.bid_project_id;
    if (!scopeId || !bidId) return toast.error("Please select a scope first");

    const basePayload = {
      data: [],
      date: moment(form.date).format("YYYY-MM-DD"),
      message: form.description,
      name: form.name,
    };

    setLoading(true);
    try {
      /* Phase 1 of create */
      if (isCreateMode && !createdUpdateId) {
        const filesMap = {};
        uploadedFiles
          .filter((f) => f.file)
          .forEach((item, index) => {
            filesMap[`file_${index + 1}`] = item.file;
          });
        const res = await createScopeUpdate(scopeId, bidId, {
          ...basePayload,
          files: filesMap,
        });
        const newId = res?.data?.update_id ?? res?.data?.id ?? res?.update_id;
        if (res?.success && newId) {
          setCreatedUpdateId(String(newId));
          setActiveTab("activities");
          toast.success(
            res?.message || "Progress report created. Add activities and milestones."
          );
        } else {
          toast.error(res?.message || "Could not create progress report");
        }
        return;
      }

      /* All subsequent saves: PUT with sanitized milestones + file uploads */
      const refArray = singleUpdateData?.ref_data ?? [];
      const sanitized = buildSanitizedMilestones(refArray);
      const targetId = effectiveUpdateId;
      const res = await updateScopeUpdate(targetId, {
        ...basePayload,
        data: sanitized,
      });
      if (!res?.success) {
        toast.error(res?.message || "Update failed");
        return;
      }
      await uploadPendingFiles(targetId);
      await refetchSingleUpdate();
      toast.success(res?.message || "Saved");

      if (activeTab === "review") {
        onBack?.();
      } else if (activeTab === "issues") {
        setActiveTab("review");
      } else {
        const next = getNextTab(activeTab);
        if (next) setActiveTab(next);
      }
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const hasUnsavedImage = useMemo(
    () => (uploadedFiles || []).some((f) => !f.id && f.file),
    [uploadedFiles]
  );

  const handleActionSubmit = () => {
    if (activeTab === "activities") {
      if (hasUnsavedImage) {
        toast.error("You haven't saved image. Save image to proceed.");
        return;
      }
      setActiveTab("milestones");
      return;
    }
    handleSummarySubmit();
  };

  const handleReadOnlyNext = () => {
    const next = getNextTab(activeTab);
    if (next) setActiveTab(next);
  };

  const nextButtonText =
    activeTab === "review"
      ? "Submit"
      : activeTab === "summary" && isCreateMode && !createdUpdateId
      ? "Save & Continue"
      : "Next";

  const title = useMemo(() => {
    const dateSrc =
      singleUpdateData?.update_date ||
      (createdUpdateId && form.date ? form.date : null);
    const dateLabel = dateSrc ? moment(dateSrc).format("MMM DD, YYYY") : null;
    if (isCreateMode && !createdUpdateId) return "Add Progress Report";
    return dateLabel ? `Progress Report - ${dateLabel}` : "Progress Report";
  }, [isCreateMode, createdUpdateId, form.date, singleUpdateData]);

  return (
    <div className="pud-page">
      <div className="pud-header-row">
        <button
          type="button"
          className="pud-back"
          onClick={onBack}
          aria-label="Back"
        >
          <IoChevronBack size={16} />
        </button>
        <div className="pud-title">{title}</div>
      </div>

      <ProjectUpdateForm
        projectId={projectId}
        projectDetails={projectDetails}
        updateIdFromUrl={updateIdFromUrl}
        scopeIdFromUrl={scopeIdFromUrl}
        updateDateFromUrl={updateDateFromUrl}
        createdUpdateId={createdUpdateId}
        setCreatedUpdateId={setCreatedUpdateId}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isCreateMode={isCreateMode}
        isViewMode={isViewMode}
        setIsViewMode={setIsViewMode}
        hasUpdateId={hasUpdateId}
        form={form}
        setForm={setForm}
        uploadedFiles={uploadedFiles}
        setUploadedFiles={setUploadedFiles}
        selectedMilestones={selectedMilestones}
        setSelectedMilestones={setSelectedMilestones}
        milestoneProgress={milestoneProgress}
        setMilestoneProgress={setMilestoneProgress}
        milestoneNotes={milestoneNotes}
        setMilestoneNotes={setMilestoneNotes}
        onActivitiesForPayload={setActivitiesForPayload}
        singleUpdateData={singleUpdateData}
        refetchSingleUpdate={refetchSingleUpdate}
        refDataMilestones={refDataMilestones}
      />

      {!isViewMode && (
        <div className="pud-actionbar">
          {activeTab === "summary" ? (
            <button
              type="button"
              className="pud-btn"
              disabled={loading}
              onClick={onBack}
            >
              Close
            </button>
          ) : (
            <button
              type="button"
              className="pud-btn"
              disabled={loading}
              onClick={() => {
                const prev = getPrevTab(activeTab);
                if (prev) setActiveTab(prev);
              }}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="pud-btn pud-btn-primary"
            disabled={loading}
            onClick={handleActionSubmit}
          >
            {loading ? "Saving..." : nextButtonText}
          </button>
        </div>
      )}

      {isViewMode && !isCreateMode && (
        <div className="pud-actionbar">
          {activeTab !== "summary" && (
            <button
              type="button"
              className="pud-btn"
              onClick={() => {
                const prev = getPrevTab(activeTab);
                if (prev) setActiveTab(prev);
              }}
            >
              Back
            </button>
          )}
          {activeTab === "review" ? (
            <button
              type="button"
              className="pud-btn pud-btn-primary"
              onClick={onBack}
            >
              Close
            </button>
          ) : (
            <button
              type="button"
              className="pud-btn pud-btn-primary"
              disabled={!getNextTab(activeTab)}
              onClick={handleReadOnlyNext}
            >
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
}
